import React, { useState, useEffect, useMemo } from 'react';
import PageContainer from '../../../components/PageContainer';
import ForecastApiService from '../../../services/api/ForecastApiService';
import PercentuaisApiService from '../../../services/api/PercentuaisApiService';
import { Tabs, Tab, Badge, Table, Dropdown } from 'react-bootstrap';
import Loading from '../../../components/Loading';
import useAlertNotification from '../../../context/AlertNotificationContext';
import CustomModal from '../../../components/CustomModal';
import FiltroAcompanhamentoForecast from './FiltroAcompanhamentoForecast';
import { formatPeriodoLabel } from '../../../services/forecast/periodoUtils';
import { MdFileDownload, MdOutlineArrowBack } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const MESES_PT_ABBR = {
    'Janeiro': 'Jan', 'Fevereiro': 'Fev', 'Março': 'Mar', 'Abril': 'Abr',
    'Maio': 'Mai', 'Junho': 'Jun', 'Julho': 'Jul', 'Agosto': 'Ago',
    'Setembro': 'Set', 'Outubro': 'Out', 'Novembro': 'Nov', 'Dezembro': 'Dez'
};

const MESES_LABEL = Object.fromEntries(
    Object.entries(MESES_PT_ABBR).map(([k, v]) => [k.toLowerCase(), v])
);

function formatCurrency(val) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
}

function formatPercent(val) {
    return new Intl.NumberFormat('pt-BR', { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(val || 0);
}

function probVariant(prob) {
    if (prob >= 0.9) return 'success';
    if (prob >= 0.7) return 'warning';
    return 'info';
}

// Custo Total derivado por cálculo reverso a partir da Margem Líquida.
// Premissas vêm de /percentuais-financeiros como strings em pontos percentuais (ex: "34").
// Fórmula (planilha-valores.xlsx, layers 5→16):
//   ResLiq   = M × RB                       (Margem Líquida × Receita Bruta)
//   EBITDA   = ResLiq / (1 - IRPJ/CSLL)     se M > 0  |  ResLiq se M ≤ 0
//   ResOper  = EBITDA + DespOp × RecLiq + RV × RB
//   Custo    = RecLiq - ResOper
function calcCustoTotal(receitaBruta, margemLiquida, premissas) {
    if (receitaBruta == null || margemLiquida == null || !premissas) return null;
    const RB = Number(receitaBruta);
    const M = Number(margemLiquida);
    if (!isFinite(RB) || !isFinite(M)) return null;
    const toFrac = v => Number(String(v ?? '').replace(',', '.')) / 100;
    const impostosNF = toFrac(premissas.impostosResultadoBruto);
    const despOp    = toFrac(premissas.despesaOperacional);
    const rv        = toFrac(premissas.rv);
    const irpjCsll  = toFrac(premissas.impostosAntesIrCsll);
    if (![impostosNF, despOp, rv, irpjCsll].every(isFinite)) return null;
    const recLiq = RB * (1 - impostosNF);
    const resLiq = M * RB;
    const ebitda = M > 0 ? resLiq / (1 - irpjCsll) : resLiq;
    const resOper = ebitda + despOp * recLiq + rv * RB;
    return recLiq - resOper;
}

// Devolve { resOper, recLiq } por item para que o chamador agregue como Σ ResOper / Σ RecLiq.
// MC = ResultadoOperacional / ReceitaLíquida (planilha layer 10 = D23/D17).
function calcMargemContribuicaoParts(receitaBruta, margemLiquida, premissas) {
    if (receitaBruta == null || margemLiquida == null || !premissas) return null;
    const RB = Number(receitaBruta);
    const M = Number(margemLiquida);
    if (!isFinite(RB) || !isFinite(M)) return null;
    const toFrac = v => Number(String(v ?? '').replace(',', '.')) / 100;
    const impostosNF = toFrac(premissas.impostosResultadoBruto);
    const despOp    = toFrac(premissas.despesaOperacional);
    const rv        = toFrac(premissas.rv);
    const irpjCsll  = toFrac(premissas.impostosAntesIrCsll);
    if (![impostosNF, despOp, rv, irpjCsll].every(isFinite)) return null;
    const recLiq = RB * (1 - impostosNF);
    const resLiq = M * RB;
    const ebitda = M > 0 ? resLiq / (1 - irpjCsll) : resLiq;
    const resOper = ebitda + despOp * recLiq + rv * RB;
    return { resOper, recLiq };
}

// Estilo para fixar o cabeçalho da tabela ao rolar verticalmente.
// zIndex: 500 — abaixo do z-index dos dropdowns Bootstrap (1000) para que os
// menus de filtro apareçam sobre o cabeçalho fixo; acima das células fc-X (100).
const fixarThead = {
    position: 'sticky',
    top: 0,
    zIndex: 500,
    backgroundColor: '#f5f5f5',
    boxShadow: '0 4px 4px -4px rgba(0, 0, 0, 0.3)',
};

function AcompanhamentoForecastView() {
    const alert = useAlertNotification();

    const [dadosPlanejados, setDadosPlanejados] = useState(null);
    const [dadosResultado, setDadosResultado] = useState(null);
    const [loadingPlanejado, setLoadingPlanejado] = useState(false);
    const [loadingResultado, setLoadingResultado] = useState(false);
    const [filtroAplicado, setFiltroAplicado] = useState(false);
    const [filtroParams, setFiltroParams] = useState(null);
    const [abaAtiva, setAbaAtiva] = useState('planejado');
    const [filtroGestor, setFiltroGestor] = useState('');
    const [filtroCliente, setFiltroCliente] = useState('');
    const [filtroCategoria, setFiltroCategoria] = useState('');
    const [filtroProbabilidade, setFiltroProbabilidade] = useState([]);
    const [showProbDropdown, setShowProbDropdown] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [percentuais, setPercentuais] = useState(null);

    useEffect(() => {
        PercentuaisApiService.getPercents()
            .then(resp => setPercentuais(resp.data))
            .catch(() => alert.error('Erro ao buscar premissas financeiras'));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAplicarFiltro = async (params) => {
        setFiltroAplicado(true);
        setFiltroParams(params);
        setFiltroGestor('');
        setFiltroCliente('');
        setFiltroCategoria('');
        setFiltroProbabilidade([]);
        setSortConfig({ key: null, direction: 'asc' });
        setLoadingPlanejado(true);
        setLoadingResultado(true);
        setDadosPlanejados(null);
        setDadosResultado(null);

        const [resPlanejado, resResultado] = await Promise.allSettled([
            ForecastApiService.getAcompanhamentoPlanejado(params),
            ForecastApiService.getResultadoAcompanhamento(params),
        ]);

        if (resPlanejado.status === 'fulfilled') {
            setDadosPlanejados(resPlanejado.value.data);
        } else {
            alert.error('Erro ao buscar dados do Forecast Planejado');
        }
        setLoadingPlanejado(false);

        if (resResultado.status === 'fulfilled') {
            setDadosResultado(resResultado.value.data);
        } else {
            alert.error('Erro ao buscar dados do Resultado de Projeto');
        }
        setLoadingResultado(false);
    };

    const abrirFiltro = () => {
        CustomModal.show({
            title: 'Filtros',
            body: <FiltroAcompanhamentoForecast onAplicar={handleAplicarFiltro} />,
        });
    };

    useEffect(() => {
        abrirFiltro();
    }, []);

    // Volta para o estado inicial desta mesma página (sem forecast selecionado) e reabre
    // o modal de Filtros — não usa history.goBack(): o histórico de navegação real do
    // usuário nem sempre tem uma entrada útil dentro do app.
    const handleVoltar = () => {
        setFiltroAplicado(false);
        setFiltroParams(null);
        setDadosPlanejados(null);
        setDadosResultado(null);
        setFiltroGestor('');
        setFiltroCliente('');
        setFiltroCategoria('');
        setFiltroProbabilidade([]);
        setSortConfig({ key: null, direction: 'asc' });
        abrirFiltro();
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) return <FaSort className="ml-1" style={{ opacity: 0.3 }} />;
        return sortConfig.direction === 'asc'
            ? <FaSortUp className="ml-1" />
            : <FaSortDown className="ml-1" />;
    };

    const toggleProbabilidade = (val) => {
        setFiltroProbabilidade(prev =>
            prev.includes(val) ? prev.filter(p => p !== val) : [...prev, val]
        );
    };

    const mesesPlanejado = dadosPlanejados?.meses || [];
    // Mesma granularidade da aba Planejado — só muda a capitalização, que é como
    // dadosResultado.result[pk][ano][mes] indexa os meses (nome completo capitalizado).
    const mesesRealizado = mesesPlanejado.map(m => m.charAt(0).toUpperCase() + m.slice(1));
    // ── Opções dos filtros inline ──────────────────────────────────────
    const opcoesGestor = useMemo(() => {
        const set = new Set();
        dadosPlanejados?.porGP?.forEach(g => g.gp && set.add(g.gp));
        Object.values(dadosResultado?.gestoresPorProjeto || {}).forEach(g => g && set.add(g));
        return Array.from(set).sort();
    }, [dadosPlanejados, dadosResultado]);

    const opcoesCliente = useMemo(() => {
        const set = new Set();
        dadosPlanejados?.porGP?.forEach(g => g.itens.forEach(i => i.cliente && set.add(i.cliente)));
        return Array.from(set).sort();
    }, [dadosPlanejados]);

    const opcoesCategoria = useMemo(() => {
        const set = new Set();
        dadosPlanejados?.porGP?.forEach(g => g.itens.forEach(i => i.categoria && set.add(i.categoria)));
        return Array.from(set).sort();
    }, [dadosPlanejados]);

    const opcoesProbabilidade = useMemo(() => {
        const set = new Set();
        dadosPlanejados?.porGP?.forEach(g =>
            g.itens.forEach(i => { if (i.probabilidade != null) set.add(Number(i.probabilidade)); })
        );
        return Array.from(set).sort((a, b) => a - b);
    }, [dadosPlanejados]);

    // ── Planejado: lista plana filtrada + ordenada ─────────────────────
    const planejadoOrdenado = useMemo(() => {
        if (!dadosPlanejados?.porGP) return [];
        let flat = dadosPlanejados.porGP.flatMap(gpData =>
            gpData.itens
                .filter(item => {
                    if (filtroGestor && gpData.gp !== filtroGestor) return false;
                    if (filtroCliente && item.cliente !== filtroCliente) return false;
                    if (filtroCategoria && item.categoria !== filtroCategoria) return false;
                    if (filtroProbabilidade.length > 0 && !filtroProbabilidade.includes(Number(item.probabilidade))) return false;
                    return true;
                })
                .map(item => ({ gpData, item }))
        );
        if (sortConfig.key) {
            flat = [...flat].sort((a, b) => {
                const d = sortConfig.direction === 'asc' ? 1 : -1;
                switch (sortConfig.key) {
                    case 'gestor': return d * (a.gpData.gp || '').localeCompare(b.gpData.gp || '');
                    case 'cliente': return d * (a.item.cliente || '').localeCompare(b.item.cliente || '');
                    case 'categoria': return d * (a.item.categoria || '').localeCompare(b.item.categoria || '');
                    case 'probabilidade': return d * ((a.item.probabilidade || 0) - (b.item.probabilidade || 0));
                    default: return 0;
                }
            });
        }
        return flat;
    }, [dadosPlanejados, filtroGestor, filtroCliente, filtroCategoria, filtroProbabilidade, sortConfig]);

    const totaisPlanejado = useMemo(() => {
        const meses = {};
        let somaTotal = 0, somaPonderada = 0, custoTotal = 0;
        let receitaComMargem = 0, resultadoLiquidoTotal = 0;
        let resOperSomado = 0, recLiqSomado = 0;
        for (const { item } of planejadoOrdenado) {
            for (const mes of mesesPlanejado) {
                meses[mes] = (meses[mes] || 0) + (Number(item.meses?.[mes]) || 0);
            }
            somaTotal += Number(item.somaTotal) || 0;
            somaPonderada += Number(item.somaPonderada) || 0;
            const ct = calcCustoTotal(item.somaTotal, item.margemLiquida, percentuais);
            if (ct != null) custoTotal += ct;
            const rb = Number(item.somaTotal);
            const m = Number(item.margemLiquida);
            if (item.margemLiquida != null && isFinite(rb) && isFinite(m)) {
                receitaComMargem += rb;
                resultadoLiquidoTotal += m * rb;
            }
            const mc = calcMargemContribuicaoParts(item.somaTotal, item.margemLiquida, percentuais);
            if (mc != null) {
                resOperSomado += mc.resOper;
                recLiqSomado += mc.recLiq;
            }
        }
        const margemLiquidaTotal = receitaComMargem > 0
            ? resultadoLiquidoTotal / receitaComMargem
            : null;
        const margemContribuicaoTotal = recLiqSomado > 0
            ? resOperSomado / recLiqSomado
            : null;
        return { meses, somaTotal, somaPonderada, custoTotal, margemLiquidaTotal, margemContribuicaoTotal };
    }, [planejadoOrdenado, mesesPlanejado, percentuais]);

    // ── Realizado: filtrado por gestor ─────────────────────────────────
    const projetosPorGestorFiltrado = useMemo(() => {
        if (!dadosResultado?.result) return {};
        const map = {};
        for (const pk of Object.keys(dadosResultado.result)) {
            const g = dadosResultado.gestoresPorProjeto?.[pk] || 'Sem Gestor';
            if (filtroGestor && g !== filtroGestor) continue;
            if (!map[g]) map[g] = [];
            map[g].push(pk);
        }
        return map;
    }, [dadosResultado, filtroGestor]);

    const temResultadoFiltrado = Object.keys(projetosPorGestorFiltrado).length > 0;

    const totaisRealizado = useMemo(() => {
        if (!dadosResultado?.result || !dadosPlanejados) return { meses: {}, total: 0, custoTotal: 0, margemLiquidaTotal: null, margemContribuicaoTotal: null };
        const ano = String(dadosPlanejados.ano);
        const meses = {};
        let total = 0;
        let custoTotal = 0;
        let resultadoLiquidoSomado = 0;
        let resultadoBrutoSomado = 0, receitaLiquidaSomada = 0;
        for (const projetos of Object.values(projetosPorGestorFiltrado)) {
            for (const pk of projetos) {
                for (const mes of mesesRealizado) {
                    const val = parseFloat(dadosResultado.result[pk]?.[ano]?.[mes]?.[0]?.receita_bruta) || 0;
                    meses[mes] = (meses[mes] || 0) + val;
                }
                const totaisAno = dadosResultado.totaisPorAno?.[pk]?.[ano] || {};
                total      += parseFloat(totaisAno.receitaBrutaTotal) || 0;
                custoTotal += parseFloat(totaisAno.totalCustoVenda) || 0;
                resultadoLiquidoSomado += parseFloat(totaisAno.resultadoLiquidoTotal) || 0;
                resultadoBrutoSomado   += parseFloat(totaisAno.resultadoBrutoTotal) || 0;
                receitaLiquidaSomada   += parseFloat(totaisAno.receitaLiquidaTotal) || 0;
            }
        }
        const margemLiquidaTotal = total > 0 ? resultadoLiquidoSomado / total : null;
        const margemContribuicaoTotal = receitaLiquidaSomada > 0
            ? resultadoBrutoSomado / receitaLiquidaSomada
            : null;
        return { meses, total, custoTotal, margemLiquidaTotal, margemContribuicaoTotal };
    }, [dadosResultado, projetosPorGestorFiltrado, mesesRealizado, dadosPlanejados]);

    // ── Export Excel ───────────────────────────────────────────────────
    const exportToExcel = async () => {
        if (!filtroParams || !dadosPlanejados) return;
        try {
            const workbook = new ExcelJS.Workbook();
            const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } };
            const TOTAL_FILL  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
            const BORDER = {
                top:    { style: 'thin', color: { argb: 'FFAAAAAA' } },
                left:   { style: 'thin', color: { argb: 'FFAAAAAA' } },
                bottom: { style: 'thin', color: { argb: 'FFAAAAAA' } },
                right:  { style: 'thin', color: { argb: 'FFAAAAAA' } },
            };
            const styleHeader = (row) => row.eachCell(cell => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = HEADER_FILL;
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                cell.border = BORDER;
            });
            const styleData = (row) => row.eachCell(cell => {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = BORDER;
            });
            const styleTotal = (row) => row.eachCell(cell => {
                cell.font = { bold: true };
                cell.fill = TOTAL_FILL;
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = BORDER;
            });
            const autoWidth = (ws) => ws.columns.forEach(col => {
                let max = 10;
                col.eachCell({ includeEmpty: true }, cell => {
                    const len = cell.value != null ? cell.value.toString().length : 0;
                    if (len > max) max = len;
                });
                col.width = max + 2;
            });

            const ano = dadosPlanejados?.ano;
            const nomePeriodo = formatPeriodoLabel(dadosPlanejados?.tipoPeriodo, dadosPlanejados?.periodoNumero);
            const nomeArquivo = `Acompanhamento Forecast - ${nomePeriodo} ${ano}.xlsx`;

            // ── Aba Forecast ───────────────────────────────────────────
            const wsPlanejado = workbook.addWorksheet('Forecast');
            const headerPlanejado = ['#', 'Gestor', 'Cliente', 'Categoria', 'Prob.',
                ...mesesPlanejado.map(m => MESES_LABEL[m] || m),
                'Receita Bruta', 'Custo Total', 'Receita Bruta Ponderada', 'Margem Líquida'];
            styleHeader(wsPlanejado.addRow(headerPlanejado));

            if (planejadoOrdenado.length > 0) {
                const MOEDA_FMT = '"R$" #,##0.00';
                const PERC_FMT = '0.00%';
                const mesOffset = 6;

                planejadoOrdenado.forEach(({ gpData, item }, idx) => {
                    const custoTotalItem = calcCustoTotal(item.somaTotal, item.margemLiquida, percentuais);
                    const dataRow = wsPlanejado.addRow([
                        idx + 1,
                        gpData.gp,
                        item.cliente,
                        item.categoria,
                        item.probabilidade != null ? Number(item.probabilidade) : null,
                        ...mesesPlanejado.map(m => item.meses?.[m] ? parseFloat(item.meses[m]) : null),
                        item.somaTotal != null ? parseFloat(item.somaTotal) : null,
                        custoTotalItem != null ? custoTotalItem : null,
                        item.somaPonderada != null ? parseFloat(item.somaPonderada) : null,
                        item.margemLiquida != null ? Number(item.margemLiquida) : null,
                    ]);
                    styleData(dataRow);
                    dataRow.getCell(5).numFmt = PERC_FMT;
                    mesesPlanejado.forEach((_, i) => { dataRow.getCell(mesOffset + i).numFmt = MOEDA_FMT; });
                    dataRow.getCell(mesOffset + mesesPlanejado.length).numFmt = MOEDA_FMT;
                    dataRow.getCell(mesOffset + mesesPlanejado.length + 1).numFmt = MOEDA_FMT;
                    dataRow.getCell(mesOffset + mesesPlanejado.length + 2).numFmt = MOEDA_FMT;
                    dataRow.getCell(mesOffset + mesesPlanejado.length + 3).numFmt = PERC_FMT;
                });

                const totRow = wsPlanejado.addRow([
                    '', 'Total', '', '', '',
                    ...mesesPlanejado.map(m => totaisPlanejado.meses[m] || 0),
                    totaisPlanejado.somaTotal,
                    totaisPlanejado.custoTotal,
                    totaisPlanejado.somaPonderada,
                    totaisPlanejado.margemLiquidaTotal,
                ]);
                styleTotal(totRow);
                mesesPlanejado.forEach((_, i) => { totRow.getCell(mesOffset + i).numFmt = MOEDA_FMT; });
                totRow.getCell(mesOffset + mesesPlanejado.length).numFmt = MOEDA_FMT;
                totRow.getCell(mesOffset + mesesPlanejado.length + 1).numFmt = MOEDA_FMT;
                totRow.getCell(mesOffset + mesesPlanejado.length + 2).numFmt = MOEDA_FMT;
                totRow.getCell(mesOffset + mesesPlanejado.length + 3).numFmt = PERC_FMT;
            }
            autoWidth(wsPlanejado);

            // ── Aba Realizado ──────────────────────────────────────────
            const wsRealizado = workbook.addWorksheet('Realizado');
            const headerRealizado = ['#', 'Gestor', 'Cliente', 'Categoria',
                ...mesesRealizado.map(m => MESES_PT_ABBR[m] || m),
                'Receita Bruta', 'Custo Total', 'Margem Líquida'];
            styleHeader(wsRealizado.addRow(headerRealizado));

            if (temResultadoFiltrado) {
                const MOEDA_FMT = '"R$" #,##0.00';
                const PERC_FMT = '0.00%';
                const anoStr = String(ano);
                const mesOffset = 5; // cols fixas: #, Gestor, Cliente, Categoria

                let row = 0;
                Object.entries(projetosPorGestorFiltrado).forEach(([gestor, projetos]) => {
                    projetos.forEach(pk => {
                        row++;
                        const totaisAno = dadosResultado.totaisPorAno?.[pk]?.[anoStr] || {};
                        const dataRow = wsRealizado.addRow([
                            row,
                            gestor,
                            pk,
                            dadosResultado.categoriasPorProjeto?.[pk] || '',
                            ...mesesRealizado.map(mes => {
                                const v = parseFloat(dadosResultado.result[pk]?.[anoStr]?.[mes]?.[0]?.receita_bruta) || 0;
                                return v || null;
                            }),
                            totaisAno.receitaBrutaTotal != null ? parseFloat(totaisAno.receitaBrutaTotal) : null,
                            totaisAno.totalCustoVenda != null ? parseFloat(totaisAno.totalCustoVenda) : null,
                            totaisAno.totalMargemLiquida != null ? Number(totaisAno.totalMargemLiquida) : null,
                        ]);
                        styleData(dataRow);
                        mesesRealizado.forEach((_, i) => { dataRow.getCell(mesOffset + i).numFmt = MOEDA_FMT; });
                        dataRow.getCell(mesOffset + mesesRealizado.length).numFmt = MOEDA_FMT;
                        dataRow.getCell(mesOffset + mesesRealizado.length + 1).numFmt = MOEDA_FMT;
                        dataRow.getCell(mesOffset + mesesRealizado.length + 2).numFmt = PERC_FMT;
                    });
                });

                const totRow = wsRealizado.addRow([
                    '', 'Total', '', '',
                    ...mesesRealizado.map(m => totaisRealizado.meses[m] || 0),
                    totaisRealizado.total || null,
                    totaisRealizado.custoTotal || null,
                    totaisRealizado.margemLiquidaTotal,
                ]);
                styleTotal(totRow);
                mesesRealizado.forEach((_, i) => { totRow.getCell(mesOffset + i).numFmt = MOEDA_FMT; });
                totRow.getCell(mesOffset + mesesRealizado.length).numFmt = MOEDA_FMT;
                totRow.getCell(mesOffset + mesesRealizado.length + 1).numFmt = MOEDA_FMT;
                totRow.getCell(mesOffset + mesesRealizado.length + 2).numFmt = PERC_FMT;
            }
            autoWidth(wsRealizado);

            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), nomeArquivo);
        } catch (err) {
            alert.error('Erro ao exportar o arquivo Excel');
        }
    };

    const actions = [
        {
            label: 'Voltar',
            icon: <MdOutlineArrowBack />,
            color: 'info',
            type: 'button',
            onClick: handleVoltar,
        },
        {
            label: 'Exportar Excel',
            icon: <MdFileDownload />,
            color: 'success',
            onClick: exportToExcel,
            disabled: !filtroAplicado || loadingPlanejado || loadingResultado || !dadosPlanejados,
        },
    ];

    const thSort = { cursor: 'pointer' };

    return (
        <PageContainer title="Acompanhamento do Forecast" actions={actions}>

            {/* ── Cabeçalho com info da importação ── */}
            {filtroParams && (
                <div className="d-flex flex-wrap align-items-center mb-3"
                    style={{ fontSize: '0.85rem', color: '#555', gap: '0.5rem 1.25rem' }}>
                    <span>
                        <b>Planilha:</b>{' '}
                        {dadosPlanejados?.arquivo || (loadingPlanejado ? '...' : '—')}
                    </span>
                    <span style={{ color: '#bbb' }}>|</span>
                    <span>
                        <b>Importação:</b>{' '}
                        {dadosPlanejados?.importadoEm
                            ? new Date(dadosPlanejados.importadoEm).toLocaleDateString('pt-BR')
                            : (loadingPlanejado ? '...' : '—')}
                    </span>
                    <span style={{ color: '#bbb' }}>|</span>
                    <span>
                        <b>Período:</b>{' '}
                        {dadosPlanejados
                            ? `${formatPeriodoLabel(dadosPlanejados.tipoPeriodo, dadosPlanejados.periodoNumero)} de ${dadosPlanejados.ano}`
                            : (loadingPlanejado ? '...' : '—')}
                    </span>
                </div>
            )}

            {/* ── Quadro de resumo ── */}
            {filtroAplicado && !loadingPlanejado && !loadingResultado && (
                <Table bordered style={{ background: '#e1eaf3', width: '100%', tableLayout: 'fixed' }} className="mb-3">
                    <thead>
                        <tr>
                            <th></th>
                            <th className="text-center">Forecast</th>
                            <th className="text-center">Realizado</th>
                            <th className="text-center">Meta Atingida</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="font-weight-bold">Margem Líquida</td>
                            <td className="text-center">
                                <b>{totaisPlanejado.margemLiquidaTotal != null
                                    ? formatPercent(totaisPlanejado.margemLiquidaTotal)
                                    : <span className="text-muted">—</span>}</b>
                            </td>
                            <td className="text-center">
                                <b>{totaisRealizado.margemLiquidaTotal != null
                                    ? formatPercent(totaisRealizado.margemLiquidaTotal)
                                    : <span className="text-muted">—</span>}</b>
                            </td>
                            <td className="text-center">
                                <b>
                                    {totaisPlanejado.margemLiquidaTotal != null && totaisPlanejado.margemLiquidaTotal !== 0 && totaisRealizado.margemLiquidaTotal != null
                                        ? formatPercent(totaisRealizado.margemLiquidaTotal / totaisPlanejado.margemLiquidaTotal)
                                        : <span className="text-muted">—</span>}
                                </b>
                            </td>
                        </tr>
                        <tr>
                            <td className="font-weight-bold">Margem de Contribuição</td>
                            <td className="text-center">
                                <b>{totaisPlanejado.margemContribuicaoTotal != null
                                    ? formatPercent(totaisPlanejado.margemContribuicaoTotal)
                                    : <span className="text-muted">—</span>}</b>
                            </td>
                            <td className="text-center">
                                <b>{totaisRealizado.margemContribuicaoTotal != null
                                    ? formatPercent(totaisRealizado.margemContribuicaoTotal)
                                    : <span className="text-muted">—</span>}</b>
                            </td>
                            <td className="text-center">
                                <b>
                                    {totaisPlanejado.margemContribuicaoTotal != null && totaisPlanejado.margemContribuicaoTotal !== 0 && totaisRealizado.margemContribuicaoTotal != null
                                        ? formatPercent(totaisRealizado.margemContribuicaoTotal / totaisPlanejado.margemContribuicaoTotal)
                                        : <span className="text-muted">—</span>}
                                </b>
                            </td>
                        </tr>
                    </tbody>
                </Table>
            )}

            {/* ── Filtros inline ── */}
            {filtroAplicado && (
                <div className="d-flex flex-wrap mb-3" style={{ gap: '0.5rem' }}>
                    <Dropdown>
                        <Dropdown.Toggle variant="light">
                            Gestor: <b>{filtroGestor || 'Todos'}</b>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setFiltroGestor('')}>Todos</Dropdown.Item>
                            {opcoesGestor.map(g => (
                                <Dropdown.Item key={g} onClick={() => setFiltroGestor(g)}>{g}</Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>

                    <Dropdown>
                        <Dropdown.Toggle variant="light">
                            Cliente: <b>{filtroCliente || 'Todos'}</b>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setFiltroCliente('')}>Todos</Dropdown.Item>
                            {opcoesCliente.map(c => (
                                <Dropdown.Item key={c} onClick={() => setFiltroCliente(c)}>{c}</Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>

                    <Dropdown>
                        <Dropdown.Toggle variant="light">
                            Categoria: <b>{filtroCategoria || 'Todas'}</b>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setFiltroCategoria('')}>Todas</Dropdown.Item>
                            {opcoesCategoria.map(c => (
                                <Dropdown.Item key={c} onClick={() => setFiltroCategoria(c)}>{c}</Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>

                    {/* Multi-select probability filter */}
                    <Dropdown show={showProbDropdown} onToggle={setShowProbDropdown}>
                        <Dropdown.Toggle variant="light">
                            Probabilidade:{' '}
                            <b>
                                {filtroProbabilidade.length > 0
                                    ? filtroProbabilidade.map(p => formatPercent(p)).join(', ')
                                    : 'Todas'}
                            </b>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <div
                                className="dropdown-item"
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={e => { e.stopPropagation(); setFiltroProbabilidade([]); }}
                            >
                                <input
                                    type="checkbox"
                                    readOnly
                                    checked={filtroProbabilidade.length === 0}
                                    style={{ marginRight: 8, pointerEvents: 'none' }}
                                />
                                Todas
                            </div>
                            {opcoesProbabilidade.map(p => (
                                <div
                                    key={p}
                                    className="dropdown-item"
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={e => { e.stopPropagation(); toggleProbabilidade(p); }}
                                >
                                    <input
                                        type="checkbox"
                                        readOnly
                                        checked={filtroProbabilidade.includes(p)}
                                        style={{ marginRight: 8, pointerEvents: 'none' }}
                                    />
                                    {formatPercent(p)}
                                </div>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            )}

            {/* ── Abas ── */}
            {filtroAplicado && (
                <Tabs activeKey={abaAtiva} onSelect={k => setAbaAtiva(k)} className="mb-0">

                    {/* ── Aba: Forecast ── */}
                    <Tab eventKey="planejado" title="Forecast">
                        {loadingPlanejado ? (
                            <Loading message="Carregando..." />
                        ) : planejadoOrdenado.length > 0 ? (
                            <div
                                id="tabela-forecast-planejado"
                                style={{ overflowY: 'auto', height: 'calc(100vh - 70px)' }}
                            >
                                <Table striped bordered hover style={{ tableLayout: 'fixed' }}>
                                    <thead style={fixarThead}>
                                        <tr>
                                            <th className="fc-1 no-wrap text-center" style={{ width: 60 }}>#</th>
                                            <th className="fc-2 no-wrap text-center" style={{ ...thSort, width: 180 }} onClick={() => handleSort('gestor')}>
                                                Gestor <SortIcon column="gestor" />
                                            </th>
                                            <th className="fc-3 no-wrap text-center" style={{ ...thSort, width: 180 }} onClick={() => handleSort('cliente')}>
                                                Cliente <SortIcon column="cliente" />
                                            </th>
                                            <th className="fc-4 no-wrap text-center" style={{ ...thSort, width: 150 }} onClick={() => handleSort('categoria')}>
                                                Categoria <SortIcon column="categoria" />
                                            </th>
                                            <th className="fc-5 no-wrap text-center" style={{ ...thSort, width: 100 }} onClick={() => handleSort('probabilidade')}>
                                                Prob. <SortIcon column="probabilidade" />
                                            </th>
                                            {mesesPlanejado.map(mes => (
                                                <th key={mes} className="no-wrap text-center" style={{ width: 120 }}>
                                                    {MESES_LABEL[mes]}
                                                </th>
                                            ))}
                                            <th className="no-wrap text-center" style={{ width: 140 }}>Receita Bruta</th>
                                            <th className="no-wrap text-center" style={{ width: 140 }}>Custo Total</th>
                                            <th className="no-wrap text-center" style={{ width: 190 }}>Receita Bruta Ponderada</th>
                                            <th className="no-wrap text-center" style={{ width: 140 }}>Margem Líquida</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {planejadoOrdenado.map(({ gpData, item }, idx) => {
                                            const custoTotalItem = calcCustoTotal(item.somaTotal, item.margemLiquida, percentuais);
                                            return (
                                                <tr key={`${gpData.gp}-${idx}`}>
                                                    <td className="fc-1 no-wrap text-center">{idx + 1}</td>
                                                    <td className="fc-2 no-wrap text-center font-weight-bold" title={gpData.gp}>{gpData.gp}</td>
                                                    <td className="fc-3 no-wrap text-center" title={item.cliente}>{item.cliente}</td>
                                                    <td className="fc-4 no-wrap text-center" title={item.categoria}>{item.categoria}</td>
                                                    <td className="fc-5 no-wrap text-center">
                                                        <Badge variant={probVariant(item.probabilidade)}>
                                                            {formatPercent(item.probabilidade)}
                                                        </Badge>
                                                    </td>
                                                    {mesesPlanejado.map(mes => (
                                                        <td key={mes} className="no-wrap text-center">
                                                            {item.meses?.[mes]
                                                                ? formatCurrency(item.meses[mes])
                                                                : <span className="text-muted">—</span>}
                                                        </td>
                                                    ))}
                                                    <td className="no-wrap text-center font-weight-bold">
                                                        {formatCurrency(item.somaTotal)}
                                                    </td>
                                                    <td className="no-wrap text-center">
                                                        {custoTotalItem != null
                                                            ? formatCurrency(custoTotalItem)
                                                            : <span className="text-muted">—</span>}
                                                    </td>
                                                    <td className="no-wrap text-center">
                                                        {formatCurrency(item.somaPonderada)}
                                                    </td>
                                                    <td className="no-wrap text-center">
                                                        {item.margemLiquida != null
                                                            ? formatPercent(item.margemLiquida)
                                                            : <span className="text-muted">—</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="font-weight-bold">
                                            <td colSpan={5} className="fc-1 no-wrap"><b>Total</b></td>
                                            {mesesPlanejado.map(mes => (
                                                <td key={mes} className="no-wrap text-center">
                                                    <b>{formatCurrency(totaisPlanejado.meses[mes] || 0)}</b>
                                                </td>
                                            ))}
                                            <td className="no-wrap text-center">
                                                <b>{formatCurrency(totaisPlanejado.somaTotal)}</b>
                                            </td>
                                            <td className="no-wrap text-center">
                                                <b>{formatCurrency(totaisPlanejado.custoTotal)}</b>
                                            </td>
                                            <td className="no-wrap text-center">
                                                <b>{formatCurrency(totaisPlanejado.somaPonderada)}</b>
                                            </td>
                                            <td className="no-wrap text-center">
                                                {totaisPlanejado.margemLiquidaTotal != null
                                                    ? <b>{formatPercent(totaisPlanejado.margemLiquidaTotal)}</b>
                                                    : null}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center p-5">
                                <p className="text-muted">
                                    Não há dados de Forecast Planejado para o período selecionado
                                </p>
                            </div>
                        )}
                    </Tab>

                    {/* ── Aba: Resultado de Projeto ── */}
                    <Tab eventKey="realizado" title="Realizado">
                        {loadingResultado ? (
                            <Loading message="Carregando..." />
                        ) : temResultadoFiltrado ? (
                            <div
                                id="tabela-forecast-realizado"
                                style={{ overflowY: 'auto', height: 'calc(100vh - 70px)' }}
                            >
                                <Table striped bordered hover style={{ tableLayout: 'fixed' }}>
                                    <thead style={fixarThead}>
                                        <tr>
                                            <th className="fc-1 no-wrap text-center" style={{ width: 60 }}>#</th>
                                            <th className="fc-2 no-wrap text-center" style={{ width: 180 }}>Gestor</th>
                                            <th className="fc-3 no-wrap text-center" style={{ width: 180 }}>Cliente</th>
                                            <th className="fc-4 no-wrap text-center" style={{ width: 150 }}>Categoria</th>
                                            {mesesRealizado.map(mes => (
                                                <th key={mes} className="no-wrap text-center" style={{ width: 120 }}>
                                                    {MESES_PT_ABBR[mes]}
                                                </th>
                                            ))}
                                            <th className="no-wrap text-center" style={{ width: 140 }}>Receita Bruta</th>
                                            <th className="no-wrap text-center" style={{ width: 140 }}>Custo Total</th>
                                            <th className="no-wrap text-center" style={{ width: 140 }}>Margem Líquida</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const ano = String(dadosPlanejados?.ano);
                                            let row = 0;
                                            return Object.entries(projetosPorGestorFiltrado).flatMap(([gestor, projetos]) =>
                                                projetos.map(pk => {
                                                    row++;
                                                    const totaisAno = dadosResultado.totaisPorAno?.[pk]?.[ano] || {};
                                                    return (
                                                        <tr key={`${gestor}::${pk}`}>
                                                            <td className="fc-1 no-wrap text-center">{row}</td>
                                                            <td className="fc-2 no-wrap text-center font-weight-bold" title={gestor}>{gestor}</td>
                                                            <td className="fc-3 no-wrap text-center" title={pk}>{pk}</td>
                                                            <td className="fc-4 no-wrap text-center" title={dadosResultado.categoriasPorProjeto?.[pk] || undefined}>
                                                                {dadosResultado.categoriasPorProjeto?.[pk] || <span className="text-muted">—</span>}
                                                            </td>
                                                            {mesesRealizado.map(mes => {
                                                                const val = parseFloat(dadosResultado.result[pk]?.[ano]?.[mes]?.[0]?.receita_bruta) || 0;
                                                                return (
                                                                    <td key={mes} className="no-wrap text-center">
                                                                        {val ? formatCurrency(val) : <span className="text-muted">—</span>}
                                                                    </td>
                                                                );
                                                            })}
                                                            <td className="no-wrap text-center font-weight-bold">
                                                                {formatCurrency(totaisAno.receitaBrutaTotal)}
                                                            </td>
                                                            <td className="no-wrap text-center">
                                                                {totaisAno.totalCustoVenda != null
                                                                    ? formatCurrency(totaisAno.totalCustoVenda)
                                                                    : <span className="text-muted">—</span>}
                                                            </td>
                                                            <td className="no-wrap text-center">
                                                                {totaisAno.totalMargemLiquida != null
                                                                    ? formatPercent(totaisAno.totalMargemLiquida)
                                                                    : <span className="text-muted">—</span>}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            );
                                        })()}
                                    </tbody>
                                    <tfoot>
                                        <tr className="font-weight-bold">
                                            <td colSpan={4} className="fc-1 no-wrap"><b>Total</b></td>
                                            {mesesRealizado.map(mes => (
                                                <td key={mes} className="no-wrap text-center">
                                                    <b>{formatCurrency(totaisRealizado.meses[mes] || 0)}</b>
                                                </td>
                                            ))}
                                            <td className="no-wrap text-center">
                                                <b>{formatCurrency(totaisRealizado.total)}</b>
                                            </td>
                                            <td className="no-wrap text-center">
                                                <b>{formatCurrency(totaisRealizado.custoTotal)}</b>
                                            </td>
                                            <td className="no-wrap text-center">
                                                {totaisRealizado.margemLiquidaTotal != null
                                                    ? <b>{formatPercent(totaisRealizado.margemLiquidaTotal)}</b>
                                                    : null}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center p-5">
                                <p className="text-muted">
                                    Não há dados de Resultado de Projeto para o período selecionado
                                </p>
                            </div>
                        )}
                    </Tab>
                </Tabs>
            )}

            <CustomModal />
        </PageContainer>
    );
}

export default AcompanhamentoForecastView;
